/**
 * Modal "Detalhes da Sessão" — timeline de fases + gráficos por sessão OCPP.
 *
 * Consome GET /api/sessions/:id/detail, que já retorna:
 *   - meterValues[]  série completa com {ts, valueWh, powerW, soc, phase}
 *   - phases[]       intervalos contínuos com KPIs por fase (bulk, absorção,
 *                    finalização, idle). Detectado no backend a partir de SOC
 *                    ou fração da potência máxima da sessão.
 *   - kpis           agregados da sessão inteira
 *
 * Substitui o antigo botão "Recibo" na tabela Sessões e pode ser reusado na
 * tela Fases de Carga (linha clicável). Botão de download PDF continua
 * disponível como ação secundária.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
  type ChartConfiguration,
} from "chart.js";
import "chartjs-adapter-date-fns";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
);

type ChargePhase = "bulk" | "absorcao" | "finalizacao" | "idle";

interface PhaseBlock {
  phase: ChargePhase;
  startTs: string;
  endTs: string;
  durationMin: number;
  avgPowerKw: number | null;
  maxPowerKw: number | null;
  energyKwh: number | null;
  socStartPct: number | null;
  socEndPct: number | null;
  samples: number;
}

interface SessionDetail {
  session: {
    id: number;
    ocppTransactionId: number;
    cpId: string;
    chargerVendor: string | null;
    chargerModel: string | null;
    connectorId: number;
    idTag: string;
    driverName: string | null;
    startTime: string;
    stopTime: string | null;
    energyWh: number | null;
    stopReason: string | null;
  };
  kpis: {
    durationMin: number | null;
    energyKwh: number | null;
    avgPowerKw: number | null;
    maxPowerKw: number | null;
    socStartPct: number | null;
    socEndPct: number | null;
    samples: number;
  };
  meterValues: Array<{
    ts: string;
    valueWh: number;
    powerW: number | null;
    soc: number | null;
    phase: ChargePhase;
  }>;
  phases: PhaseBlock[];
}

interface Props {
  sessionId: number;
  onClose: () => void;
}

// ------------------------------------------------------------
// Formatação e mapeamento de fase → estilo
// ------------------------------------------------------------

const PHASE_LABEL: Record<ChargePhase, string> = {
  bulk: "Bulk (potência máx.)",
  absorcao: "Absorção",
  finalizacao: "Finalização",
  idle: "Pausa / idle",
};

/** Retorna a cor no design system para cada fase. Nada de hex. */
function phaseColorVar(phase: ChargePhase): string {
  switch (phase) {
    case "bulk":
      return "var(--nx-orange-500)";
    case "absorcao":
      return "var(--nx-warning, var(--nx-warn, #b8860b))";
    case "finalizacao":
      return "var(--nx-success-500)";
    case "idle":
      return "var(--nx-mute)";
  }
}

function readTokenRgb(varName: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!raw) return fallback;
  if (raw.startsWith("var(")) return fallback;
  return raw;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(minutes: number | null): string {
  if (minutes === null) return "em curso";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m.toString().padStart(2, "0")}min`;
}

// ------------------------------------------------------------
// Componente
// ------------------------------------------------------------

export function SessionDetailModal({ sessionId, onClose }: Props) {
  const [data, setData] = useState<SessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Um único gráfico combinado com dual-axis: potência (kW) no eixo Y
  // esquerdo (laranja) e SOC (%) no eixo Y direito (verde). O usuário
  // ganha correlação visual imediata entre estágio de recarga e SOC.
  const chartCanvasRef = useRef<HTMLCanvasElement>(null);
  const chartsRef = useRef<Chart[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setError(null);
    setData(null);
    fetch(`/api/sessions/${sessionId}/detail`, {
      credentials: "same-origin",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`http_${r.status}`);
        return r.json();
      })
      .then((body: SessionDetail) => {
        if (alive) setData(body);
      })
      .catch((err) => {
        if (alive) setError((err as Error).message ?? "load_failed");
      });
    return () => {
      alive = false;
    };
  }, [sessionId]);

  useEffect(() => {
    chartsRef.current.forEach((c) => c.destroy());
    chartsRef.current = [];
    if (!data || data.meterValues.length === 0) return;

    const orange = readTokenRgb("--nx-orange-500", "rgb(232,105,42)");
    const success = readTokenRgb("--nx-success-500", "rgb(16,153,84)");
    const mute = readTokenRgb("--nx-mute", "rgb(115,125,150)");
    const line = readTokenRgb("--nx-line", "rgb(220,225,235)");

    // Datasets estilo série temporal — Chart.js entende {x, y}
    const powerData = data.meterValues.map((m) => ({
      x: new Date(m.ts).getTime(),
      y: m.powerW !== null ? Number((m.powerW / 1000).toFixed(2)) : null,
    }));
    const socData = data.meterValues.map((m) => ({
      x: new Date(m.ts).getTime(),
      y: m.soc,
    }));

    const commonScales = {
      x: {
        type: "time" as const,
        time: { unit: "minute" as const },
        grid: { color: line, drawTicks: false },
        ticks: { color: mute, maxRotation: 0, font: { size: 10 } },
      },
    };
    const commonPlugins = {
      legend: { display: false },
      tooltip: { intersect: false, mode: "index" as const },
    };

    if (chartCanvasRef.current) {
      const cfg: ChartConfiguration<"line"> = {
        type: "line",
        data: {
          datasets: [
            {
              label: "Potência (kW)",
              data: powerData as never,
              borderColor: orange,
              backgroundColor: orange,
              tension: 0.2,
              pointRadius: 0,
              spanGaps: true,
              fill: false,
              yAxisID: "y",
            },
            {
              label: "SOC (%)",
              data: socData as never,
              borderColor: success,
              backgroundColor: success,
              borderDash: [4, 3],
              tension: 0.2,
              pointRadius: 0,
              spanGaps: true,
              fill: false,
              yAxisID: "y1",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: { color: mute, font: { size: 11 }, boxWidth: 12 },
            },
            tooltip: { intersect: false, mode: "index" as const },
          },
          scales: {
            ...commonScales,
            y: {
              type: "linear",
              position: "left",
              title: { display: true, text: "Potência (kW)", color: orange },
              beginAtZero: true,
              grid: { color: line },
              ticks: { color: orange, font: { size: 10 } },
            },
            y1: {
              type: "linear",
              position: "right",
              title: { display: true, text: "SOC (%)", color: success },
              min: 0,
              max: 100,
              grid: { display: false },
              ticks: { color: success, font: { size: 10 } },
            },
          },
        },
      };
      chartsRef.current.push(new Chart(chartCanvasRef.current, cfg));
    }

    return () => {
      chartsRef.current.forEach((c) => c.destroy());
      chartsRef.current = [];
    };
  }, [data]);

  const chargerLine = useMemo(() => {
    if (!data) return "—";
    const s = data.session;
    return [s.chargerVendor, s.chargerModel].filter(Boolean).join(" · ") || "—";
  }, [data]);

  const totalMs = useMemo(() => {
    if (!data?.phases?.length) return 0;
    const start = new Date(data.phases[0]!.startTs).getTime();
    const end = new Date(data.phases[data.phases.length - 1]!.endTs).getTime();
    return Math.max(1, end - start);
  }, [data]);

  const canDownloadPdf = data?.session.stopTime !== null;

  return (
    <div
      className="nx-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nx-session-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="nx-modal__card"
        style={{ maxWidth: 1000, width: "min(96vw, 1000px)" }}
      >
        <div className="nx-modal__head">
          <h2 id="nx-session-modal-title" style={{ margin: 0 }}>
            {data ? (
              <>
                Sessão{" "}
                <span className="mono">#{data.session.ocppTransactionId}</span>
              </>
            ) : (
              <>
                Sessão <span className="mono">#{sessionId}</span>
              </>
            )}
          </h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="nx-modal__body" style={{ display: "grid", gap: 16 }}>
          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: "color-mix(in srgb, var(--nx-danger) 12%, transparent)",
                color: "var(--nx-danger)",
                fontSize: ".9rem",
              }}
            >
              Falha ao carregar detalhes: {error}
            </div>
          )}

          {!error && !data && (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "var(--nx-mute)",
              }}
            >
              Carregando dados da sessão…
            </div>
          )}

          {data && (
            <>
              {/* KPIs */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 10,
                }}
              >
                <Kpi
                  label="Energia"
                  value={
                    data.kpis.energyKwh !== null
                      ? `${data.kpis.energyKwh.toFixed(2)} kWh`
                      : "—"
                  }
                />
                <Kpi label="Duração" value={fmtDuration(data.kpis.durationMin)} />
                <Kpi
                  label="Potência média"
                  value={
                    data.kpis.avgPowerKw !== null
                      ? `${data.kpis.avgPowerKw.toFixed(1)} kW`
                      : "—"
                  }
                />
                <Kpi
                  label="Potência máx"
                  value={
                    data.kpis.maxPowerKw !== null
                      ? `${data.kpis.maxPowerKw.toFixed(1)} kW`
                      : "—"
                  }
                />
                <Kpi
                  label="SOC entrada → saída"
                  value={
                    data.kpis.socStartPct !== null ||
                    data.kpis.socEndPct !== null
                      ? `${data.kpis.socStartPct ?? "—"}% → ${data.kpis.socEndPct ?? "—"}%`
                      : "—"
                  }
                />
                <Kpi label="Amostras" value={String(data.kpis.samples)} />
              </div>

              {/* Timeline de fases */}
              {data.phases.length > 0 && (
                <PhaseTimeline phases={data.phases} totalMs={totalMs} />
              )}

              {/* Metadados */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 8,
                  fontSize: ".9rem",
                  padding: 12,
                  background: "var(--nx-neutral-50)",
                  borderRadius: 8,
                }}
              >
                <Meta label="Carregador" value={data.session.cpId} mono />
                <Meta
                  label="Conector"
                  value={String(data.session.connectorId)}
                />
                <Meta label="Fabricante/Modelo" value={chargerLine} />
                <Meta label="Cartão OCPP" value={data.session.idTag} mono />
                <Meta
                  label="Motorista"
                  value={data.session.driverName ?? "—"}
                />
                <Meta
                  label="Início"
                  value={fmtDateTime(data.session.startTime)}
                />
                <Meta
                  label="Fim"
                  value={fmtDateTime(data.session.stopTime)}
                />
                <Meta
                  label="Motivo"
                  value={data.session.stopReason ?? "—"}
                />
              </div>

              {/* Gráfico combinado — potência (esq) + SOC (dir) */}
              {data.meterValues.length > 0 ? (
                <ChartBlock title="Potência (kW) e SOC (%) — perfil da sessão">
                  <canvas ref={chartCanvasRef} />
                </ChartBlock>
              ) : (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: "var(--nx-mute)",
                    background: "var(--nx-neutral-50)",
                    borderRadius: 8,
                  }}
                >
                  Sem meter values registrados para esta sessão.
                </div>
              )}

              {/* Estatísticas por fase */}
              {data.phases.length > 0 && (
                <PhaseCards phases={data.phases} />
              )}

              {/* Ações */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                {canDownloadPdf && (
                  <a
                    className="nx-btn nx-btn--secondary"
                    href={`/api/reports/pdf/session/${data.session.id}`}
                    download={`nxvolt-session-${data.session.id}.pdf`}
                    style={{ textDecoration: "none" }}
                  >
                    ↓ Baixar recibo PDF
                  </a>
                )}
                <button className="nx-btn" type="button" onClick={onClose}>
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Subcomponentes
// ------------------------------------------------------------

function PhaseTimeline({
  phases,
  totalMs,
}: {
  phases: PhaseBlock[];
  totalMs: number;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: ".85rem",
          fontWeight: 600,
          color: "var(--nx-mute)",
          marginBottom: 6,
        }}
      >
        Timeline de estágios de recarga
      </div>
      <div
        role="img"
        aria-label="Barra colorida das fases de recarga em ordem temporal"
        style={{
          display: "flex",
          height: 30,
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid var(--nx-line)",
        }}
      >
        {phases.map((p, i) => {
          const start = new Date(p.startTs).getTime();
          const end = new Date(p.endTs).getTime();
          const width = ((end - start) / totalMs) * 100;
          return (
            <div
              key={i}
              style={{
                width: `${width}%`,
                background: phaseColorVar(p.phase),
                position: "relative",
                minWidth: 4,
              }}
              title={`${PHASE_LABEL[p.phase]} · ${p.durationMin} min · ${p.energyKwh?.toFixed(2) ?? "?"} kWh`}
            />
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 6,
          fontSize: ".75rem",
          color: "var(--nx-mute)",
        }}
      >
        {(["bulk", "absorcao", "finalizacao", "idle"] as ChargePhase[]).map(
          (p) => (
            <span
              key={p}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: phaseColorVar(p),
                  borderRadius: 2,
                }}
              />
              {PHASE_LABEL[p]}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

function PhaseCards({ phases }: { phases: PhaseBlock[] }) {
  return (
    <div>
      <div
        style={{
          fontSize: ".85rem",
          fontWeight: 600,
          color: "var(--nx-mute)",
          marginBottom: 6,
        }}
      >
        Detalhe por estágio
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 8,
        }}
      >
        {phases.map((p, i) => (
          <div
            key={i}
            style={{
              padding: 10,
              borderRadius: 8,
              border: "1px solid var(--nx-line)",
              background: "var(--nx-bg)",
              borderLeftWidth: 4,
              borderLeftColor: phaseColorVar(p.phase),
            }}
          >
            <div
              style={{
                fontSize: ".8rem",
                fontWeight: 700,
                color: phaseColorVar(p.phase),
              }}
            >
              {PHASE_LABEL[p.phase]}
            </div>
            <div
              style={{
                fontSize: ".75rem",
                color: "var(--nx-mute)",
                marginTop: 2,
              }}
            >
              {p.durationMin} min ·{" "}
              {p.energyKwh !== null ? `${p.energyKwh.toFixed(2)} kWh` : "—"}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 4,
                marginTop: 6,
                fontSize: ".75rem",
                color: "var(--nx-ink)",
              }}
            >
              <span>
                <span style={{ color: "var(--nx-mute)" }}>P̄:</span>{" "}
                {p.avgPowerKw !== null ? `${p.avgPowerKw.toFixed(1)} kW` : "—"}
              </span>
              <span>
                <span style={{ color: "var(--nx-mute)" }}>Pmáx:</span>{" "}
                {p.maxPowerKw !== null ? `${p.maxPowerKw.toFixed(1)} kW` : "—"}
              </span>
              <span>
                <span style={{ color: "var(--nx-mute)" }}>SOC:</span>{" "}
                {p.socStartPct ?? "—"}% → {p.socEndPct ?? "—"}%
              </span>
              <span>
                <span style={{ color: "var(--nx-mute)" }}>Amostras:</span>{" "}
                {p.samples}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        border: "1px solid var(--nx-line)",
        background: "var(--nx-bg)",
      }}
    >
      <div
        style={{
          fontSize: ".7rem",
          textTransform: "uppercase",
          letterSpacing: ".04em",
          color: "var(--nx-mute)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: "var(--nx-ink)",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <span style={{ color: "var(--nx-mute)", marginRight: 6 }}>{label}:</span>
      <span
        className={mono ? "mono" : undefined}
        style={{ color: "var(--nx-ink)" }}
      >
        {value}
      </span>
    </div>
  );
}

function ChartBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--nx-line)",
        borderRadius: 8,
        padding: 12,
        background: "var(--nx-bg)",
      }}
    >
      <div
        style={{
          fontSize: ".8rem",
          fontWeight: 600,
          color: "var(--nx-mute)",
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ position: "relative", height: 220 }}>{children}</div>
    </div>
  );
}
